import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { MapPin, Phone, Mail, Facebook, Instagram, Twitter } from 'lucide-react';

export default function Footer() {
  const { theme } = useTheme();

  const bgClass = theme === 'men' 
    ? 'bg-zinc-950 border-zinc-800 text-zinc-300' 
    : theme === 'women' 
      ? 'bg-rose-50 border-pink-100 text-rose-800' 
      : 'bg-zinc-100 border-zinc-200 text-zinc-700';

  const headingClass = theme === 'men' 
    ? 'text-white' 
    : theme === 'women' 
      ? 'text-rose-900' 
      : 'text-zinc-900';

  const linkClass = theme === 'men' 
    ? 'hover:text-blue-400' 
    : theme === 'women' 
      ? 'hover:text-pink-600' 
      : 'hover:text-zinc-900';

  const accentClass = theme === 'men' ? 'text-blue-500' : theme === 'women' ? 'text-pink-600' : 'text-zinc-900';

  return (
    <footer className={`${bgClass} border-t`} data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <Link to="/" className={`text-2xl font-bold ${accentClass} tracking-tight`}>
              <span className={theme === 'women' ? 'font-heading-women italic' : 'font-heading-men'}>
                Zenvy
              </span>
            </Link>
            <p className="mt-4 text-sm leading-relaxed">
              Premium salon booking platform connecting you with the best salons in your city. 
              Experience luxury grooming at your fingertips.
            </p>
            <div className="flex space-x-4 mt-6">
              <a href="#" className={`${linkClass} transition-colors`} aria-label="Facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className={`${linkClass} transition-colors`} aria-label="Instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" className={`${linkClass} transition-colors`} aria-label="Twitter">
                <Twitter className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className={`font-semibold ${headingClass} mb-4`}>Quick Links</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/salons/men" className={`${linkClass} transition-colors`}>
                  Men's Salons
                </Link>
              </li>
              <li>
                <Link to="/salons/women" className={`${linkClass} transition-colors`}>
                  Women's Salons
                </Link>
              </li>
              <li>
                <Link to="/about" className={`${linkClass} transition-colors`}>
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/contact" className={`${linkClass} transition-colors`}>
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* For Business */}
          <div>
            <h4 className={`font-semibold ${headingClass} mb-4`}>For Business</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/register" className={`${linkClass} transition-colors`}>
                  List Your Salon
                </Link>
              </li>
              <li>
                <a href="#" className={`${linkClass} transition-colors`}>
                  Partner Benefits
                </a>
              </li>
              <li>
                <a href="#" className={`${linkClass} transition-colors`}>
                  Success Stories
                </a>
              </li>
              <li>
                <a href="#" className={`${linkClass} transition-colors`}>
                  Partner Support
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className={`font-semibold ${headingClass} mb-4`}>Contact Us</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>123 Business Avenue, Suite 100, New York, NY 10001</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 flex-shrink-0" />
                <span>+1 (555) 123-4567</span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 flex-shrink-0" />
                <span>hello@zenvy.com</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className={`mt-12 pt-8 border-t ${theme === 'men' ? 'border-zinc-800' : 'border-zinc-200'}`}>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm">
            <p>&copy; {new Date().getFullYear()} Zenvy. All rights reserved.</p>
            <div className="flex gap-6">
              <a href="#" className={`${linkClass} transition-colors`}>Privacy Policy</a>
              <a href="#" className={`${linkClass} transition-colors`}>Terms of Service</a>
              <a href="#" className={`${linkClass} transition-colors`}>Cookie Policy</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
